import { FC } from 'react';
import { Avatar, Box, Chip, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { getTableAvatarSx } from 'utils/avatarColors';

export const getInitials = (name?: string) => {
  const value = String(name || '').trim();
  if (!value) return '?';
  const parts = value.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

export const getContactName = (row: any) => {
  if (Array.isArray(row?.contactedWith) && row.contactedWith.length > 0) {
    return (
      row.contactedWith
        .map((contact: any) => contact.fullName || contact.name || contact)
        .filter(Boolean)
        .join(', ') || '—'
    );
  }
  if (row?.contactedWith?.fullName) return row.contactedWith.fullName;
  if (typeof row?.contactedWith === 'string' && row.contactedWith.trim()) return row.contactedWith;
  if (row?.prospectName) return row.prospectName;
  return '—';
};

export const formatSalesCallType = (value?: string) => {
  if (!value) return '—';
  return String(value)
    .toLowerCase()
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

export const getSalesCallStatus = (row: any) => {
  const value = row?.nextFollowUp?.length > 0 ? row.nextFollowUp[0]?.status || 'Pending' : row?.status || 'Pending';
  const statusLower = String(value).toLowerCase();
  let color: 'success' | 'primary' | 'warning' | 'default' | 'error' = 'default';
  switch (statusLower) {
    case 'completed':
      color = 'success';
      break;
    case 'scheduled':
      color = 'primary';
      break;
    case 'pending':
      color = 'warning';
      break;
    case 'cancelled':
    case 'rejected':
      color = 'error';
      break;
    default:
      color = 'default';
  }
  return { label: value, color };
};

export const SalesCallStatusChip: FC<{ row: any }> = ({ row }) => {
  const { label, color } = getSalesCallStatus(row);
  const isPending = String(label).toLowerCase() === 'pending';
  return (
    <Chip
      label={label}
      color={isPending ? undefined : color}
      size="small"
      sx={{
        fontWeight: 500,
        fontSize: '0.75rem',
        height: '24px',
        ...(isPending
          ? {
              bgcolor: '#FFF4E5',
              color: '#B54708',
              border: '1px solid #FFD8A8',
            }
          : {}),
      }}
    />
  );
};

export const SalesCallContactCell: FC<{ row: any }> = ({ row }) => {
  const name = getContactName(row);
  const company = row?.companyName || row?.prospectCompanyName || '';
  const branch = row?.branchName || '';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.25, minWidth: 0 }}>
      <Avatar sx={getTableAvatarSx(name)}>{getInitials(name)}</Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: '#101828', lineHeight: 1.35 }}>
          {name}
        </Typography>
        {company ? (
          <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.35, fontWeight: 400 }}>{company}</Box>
        ) : null}
        {branch ? (
          <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.35, fontWeight: 400 }}>{branch}</Box>
        ) : null}
      </Box>
    </Box>
  );
};

export const SalesCallDateCell: FC<{ row: any }> = ({ row }) => {
  const date = dayjs(row?.createdAt);
  if (!row?.createdAt || !date.isValid()) return <>{'—'}</>;
  return (
    <Box sx={{ py: 0.25 }}>
      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.35 }}>
        {date.format('D MMM YYYY')}
      </Typography>
      <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.35, fontWeight: 400 }}>
        {date.format('h:mm A')}
      </Box>
      {row.createdBy ? (
        <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.35, fontWeight: 400 }}>
          by {row.createdBy}
        </Box>
      ) : null}
    </Box>
  );
};

export const getAssigneeName = (row: any) =>
  row?.assignedTo?.label || row?.assignedTo?.fullName || row?.assignedToName || '';

export const SalesCallAssigneeAvatar: FC<{ row: any }> = ({ row }) => {
  const name = getAssigneeName(row);
  if (!name) {
    return (
      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>—</Typography>
    );
  }

  return (
    <Tooltip title={name} arrow>
      <Avatar sx={getTableAvatarSx(name)}>{getInitials(name)}</Avatar>
    </Tooltip>
  );
};
